import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import StarIcon from "@mui/icons-material/Star";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  API_get_logbook_entries_report,
  API_get_logbook_entry_authors,
  API_get_logbook_columns,
  API_get_file_upload,
  API_save_report,
  API_get_report_tabs,
} from "../../../constants";
import {
  DATE_RANGE_GROUPS,
  getPresetDateRange,
  getBusinessWeekStartDayIndex,
} from "../reporting/dateRangePresets";
import { getRightworkAPI } from "../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import dayjs from "dayjs";
import { Lightbox } from "react-modal-image";
import { toast } from "react-toastify";

const LogbookReportingDisplay = (props) => {
  // Filter States
  const [selectedStores, setSelectedStores] = useState([props.homeStore]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState("yesterday");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAuthors, setSelectedAuthors] = useState([]);

  // Filter stores to only show those with logbook enabled
  const logbookEnabledStores = useMemo(() => {
    return props.storeNames.filter(store => store.logbook_enabled);
  }, [props.storeNames]);

  // Data States
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableAuthors, setAvailableAuthors] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // UI States
  const [reportStatus, setReportStatus] = useState("idle");
  const [validationErrors, setValidationErrors] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAuthors, setLoadingAuthors] = useState(false);

  // Save Report States
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState("");
  const [selectedTabForReport, setSelectedTabForReport] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [reportTabs, setReportTabs] = useState([]);

  // Lightbox States
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadingFileEntryId, setLoadingFileEntryId] = useState(null);

  // Table States
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Axios config
  const axiosConfig = getDefaultAxiosConfig();

  // Format date for API calls
  const formatDate = (date) => {
    if (!date) return "";
    const jsDate = date instanceof Date ? date : date.toDate();
    const year = jsDate.getFullYear();
    const month = String(jsDate.getMonth() + 1).padStart(2, "0");
    const day = String(jsDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch report tabs
  const fetchReportTabs = useCallback(async () => {
    try {
      const params = { store_id: props.homeStore };
      const response = await axios.get(API_get_report_tabs, { params, ...axiosConfig });
      setReportTabs(response.data || []);
    } catch (error) {
      console.error("Error fetching report tabs:", error);
      setReportTabs([]);
    }
  }, [props.homeStore]);

  // Save Report Functions
  const handleSaveReport = () => {
    if (reportStatus !== "completed") {
      return;
    }
    setShowSaveDialog(true);
  };

  const handleSaveReportConfirm = () => {
    if (!reportName.trim()) {
      return;
    }

    setSaveStatus("saving");

    const reportData = {
      name: reportName.trim(),
      report_type: "logbook",
      selected_stores: selectedStores,
      selected_metrics: [], // Empty array - logbook reports don't use metrics
      selected_categories: selectedCategories,
      selected_authors: selectedAuthors,
      selected_date_range: selectedDateRange,
      custom_start_date: selectedDateRange === "custom" ? formatDate(selectedStartDate) : null,
      custom_end_date: selectedDateRange === "custom" ? formatDate(selectedEndDate) : null,
      search_query: searchQuery.trim() || null,
      tab_id: selectedTabForReport,
      store_id: props.homeStore,
      widget_type: "table", // Default for logbook reports
    };

    axios
      .post(API_save_report, reportData, axiosConfig)
      .then((res) => {
        setSaveStatus("completed");
        setReportName("");
        setSelectedTabForReport(null);
        setShowSaveDialog(false);
        toast.success("Report saved successfully!", { autoClose: 3000 });
      })
      .catch((error) => {
        console.error("Error saving report:", error);
        setSaveStatus("error");
        toast.error("Failed to save report", { autoClose: 3000 });
      });
  };

  // Fetch available categories on mount
  const fetchAvailableCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await getRightworkAPI(API_get_logbook_columns, {
        company_id: props.homeCompany,
      });
      const columns = response.data.redux.payload.logbook_columns || [];

      const categories = [];
      columns.forEach((column) => {
        column.categories.forEach((cat) => {
          categories.push({
            ...cat,
            column_name: "Logbook",
            column_index: column.index,
          });
        });
      });

      setAvailableCategories(categories);
      // Auto-select all categories by default
      setSelectedCategories(categories.map((cat) => cat.uuid));
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  }, [props.homeCompany]);

  const fetchAvailableAuthors = useCallback(async () => {
    if (selectedStores.length === 0) return;

    setLoadingAuthors(true);
    try {
      const response = await getRightworkAPI(API_get_logbook_entry_authors, {
        store_id: props.homeStore, // For authentication
        company_id: props.homeCompany,
        store_ids: selectedStores.join(","),
      });
      const authors = response.data.redux.payload.authors || [];
      setAvailableAuthors(authors);
      // Auto-select all authors by default
      setSelectedAuthors(authors.map((author) => author.id));
    } catch (error) {
      console.error("Error fetching authors:", error);
    } finally {
      setLoadingAuthors(false);
    }
  }, [props.homeCompany, selectedStores]);

  // Initialize dates based on preset
  useEffect(() => {
    if (selectedDateRange && selectedDateRange !== "custom") {
      const businessWeekStartDayIndex = getBusinessWeekStartDayIndex(
        props.storeNames,
        props.homeStore
      );
      const { startDate, endDate } = getPresetDateRange({
        preset: selectedDateRange,
        today: new Date(),
        businessWeekStartDayIndex,
      });
      setSelectedStartDate(dayjs(startDate));
      setSelectedEndDate(dayjs(endDate));
    }
  }, [selectedDateRange]);

  // Fetch available categories on mount
  useEffect(() => {
    fetchAvailableCategories();
  }, [fetchAvailableCategories]);

  // Fetch available authors when stores change
  useEffect(() => {
    fetchAvailableAuthors();
  }, [fetchAvailableAuthors]);

  // Fetch report tabs on mount
  useEffect(() => {
    fetchReportTabs();
  }, [fetchReportTabs]);

  // Reset report status when filters change
  useEffect(() => {
    setReportStatus("idle");
    setReportData([]);
    setSummaryData(null);
  }, [selectedStores, selectedCategories, selectedAuthors, selectedStartDate, selectedEndDate, searchQuery]);

  // Validation - memoized to avoid re-calculating on every render
  const isFormValid = useMemo(() => {
    if (selectedStores.length === 0) return false;
    if (!selectedStartDate || !selectedEndDate) return false;
    if (selectedStartDate > selectedEndDate) return false;

    const daysDiff = Math.abs(
      (selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff > 1825) return false; // 5 years max

    return true;
  }, [selectedStores, selectedStartDate, selectedEndDate]);

  // Validate and set errors
  const validateForm = () => {
    const errors = [];

    if (selectedStores.length === 0) {
      errors.push("Please select at least one store");
    }

    if (!selectedStartDate || !selectedEndDate) {
      errors.push("Please select a date range");
    } else if (selectedStartDate > selectedEndDate) {
      errors.push("End date must be after start date");
    } else {
      const daysDiff = Math.abs(
        (selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24)
      );
      if (daysDiff > 1825) {
        errors.push("Date range cannot exceed 5 years");
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Get file upload for lightbox
  const get_file_upload = async (file_upload_uuid, entry_id) => {
    setLoadingFileEntryId(entry_id);
    let api_params = {
      file_upload_uuid: file_upload_uuid,
    };
    try {
      const res = await getRightworkAPI(API_get_file_upload, api_params);
      if (res.status === 200) {
        setSelectedImage(res.data?.redux?.payload?.presigned_url);
        setShowLightbox(true);
      }
    } catch (error) {
      console.error("Error fetching file upload:", error);
    } finally {
      setLoadingFileEntryId(null);
    }
  };

  // Generate Report
  const generateReport = async () => {
    if (!validateForm()) {
      return;
    }

    setReportStatus("loading");
    setReportData([]);
    setSummaryData(null);
    setPage(0);

    try {
      const params = {
        store_id: props.homeStore, // For authentication
        store_ids: selectedStores.join(","), // For query logic
        start_date: formatDate(selectedStartDate),
        end_date: formatDate(selectedEndDate),
      };

      if (selectedCategories.length > 0) {
        params.category_uuids = selectedCategories.join(",");
      }

      if (searchQuery.trim()) {
        params.search_query = searchQuery.trim();
      }

      if (selectedAuthors.length > 0) {
        params.author_user_ids = selectedAuthors.join(",");
      }

      const response = await getRightworkAPI(
        API_get_logbook_entries_report,
        params
      );

      const { entries, summary } = response.data.redux.payload;
      setReportData(entries || []);
      setSummaryData(summary || {});
      setReportStatus("completed");
    } catch (error) {
      console.error("Error generating report:", error);
      setReportStatus("error");
      setValidationErrors(["Failed to generate report. Please try again."]);
    }
  };

  // Store selection handlers
  const handleStoreChange = (event) => {
    const { value } = event.target;

    if (value.includes("all")) {
      if (selectedStores.length === logbookEnabledStores.length) {
        setSelectedStores([]);
      } else {
        setSelectedStores(logbookEnabledStores.map((store) => store.id));
      }
      return;
    }

    setSelectedStores(value);
  };

  const renderStoreValue = (selected) => {
    const selectedStoresNames = logbookEnabledStores
      .filter((store) => selected.includes(store.id))
      .map((store) => store.store_name);

    if (selectedStoresNames.length <= 2) {
      return selectedStoresNames.join(", ");
    } else {
      return `${selectedStoresNames[0]}, ${selectedStoresNames[1]}, ...`;
    }
  };

  // Category selection handlers
  const handleCategoryChange = (event) => {
    const { value } = event.target;

    if (value.includes("all")) {
      if (selectedCategories.length === availableCategories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(availableCategories.map((cat) => cat.uuid));
      }
      return;
    }

    setSelectedCategories(value);
  };

  const renderCategoryValue = (selected) => {
    if (selected.length === 0) return "No categories selected";
    if (selected.length === availableCategories.length && availableCategories.length > 0) {
      return "All categories selected";
    }
    if (selected.length === 1) {
      const cat = availableCategories.find((c) => c.uuid === selected[0]);
      return cat ? cat.name : "";
    }
    return `${selected.length} categories selected`;
  };

  // Author selection handlers
  const handleAuthorChange = (event) => {
    const { value } = event.target;

    if (value.includes("all")) {
      if (selectedAuthors.length === availableAuthors.length) {
        setSelectedAuthors([]);
      } else {
        setSelectedAuthors(availableAuthors.map((author) => author.id));
      }
      return;
    }

    setSelectedAuthors(value);
  };

  const renderAuthorValue = (selected) => {
    if (selected.length === 0) return "No authors selected";
    if (selected.length === availableAuthors.length && availableAuthors.length > 0) {
      return "All authors selected";
    }
    if (selected.length === 1) {
      const author = availableAuthors.find((a) => a.id === selected[0]);
      return author ? author.full_name : "";
    }
    return `${selected.length} authors selected`;
  };

  // Date range handlers
  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setSelectedDateRange(value);

    if (value === "custom") {
      setShowCustomDateRange(true);
    } else {
      setShowCustomDateRange(false);
    }
  };

  const toggleCustomDateRange = (show) => {
    setShowCustomDateRange(show);
    if (!show) {
      setSelectedDateRange("yesterday");
    }
  };

  // Build date range menu items
  const dateRangeMenuItems = DATE_RANGE_GROUPS.flatMap((group) => [
    <MenuItem key={`header-${group.label}`} disabled>
      <span className="text-xs font-semibold text-slate-500">
        {group.label}
      </span>
    </MenuItem>,
    ...group.options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    )),
  ]);

  // Table sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const comparator = (a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "date":
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case "store":
          aValue = a.store.name;
          bValue = b.store.name;
          break;
        case "category":
          aValue = a.category.name;
          bValue = b.category.name;
          break;
        case "author":
          aValue = a.author ? a.author.full_name : "";
          bValue = b.author ? b.author.full_name : "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    };

    return [...reportData].sort(comparator);
  }, [reportData, order, orderBy]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // Highlight search text within a string
  const highlightSearchText = (text) => {
    if (!searchQuery.trim() || !text) {
      return text;
    }

    const parts = text.split(new RegExp(`(${searchQuery.trim()})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === searchQuery.trim().toLowerCase() ? (
            <span key={index} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // Format entry value - shows both numeric and text when both exist
  // Returns JSX component with bold numeric values and star icons
  // Categories are mutable; an entry formats under the type it was RECORDED
  // with (the category's current type covers pre-backfill rows).
  const entryType = (entry) => entry.recorded_type || entry.category.type;

  const formatEntryValue = (entry) => {
    let numericValue = null;
    let numericDisplay = null;

    // Get numeric value if exists
    if (entry.value_integer !== null && entry.value_integer !== undefined && entry.value_integer !== "") {
      numericValue = parseInt(entry.value_integer);
    } else if (entry.value_float !== null && entry.value_float !== undefined && entry.value_float !== "") {
      numericValue = parseFloat(entry.value_float);
    }

    // Render numeric value based on type
    if (numericValue !== null) {
      if (entryType(entry) === "star") {
        // Render stars for star type
        numericDisplay = (
          <span className="flex items-center gap-0.5">
            {Array.from({ length: Math.min(numericValue, 5) }).map((_, i) => (
              <StarIcon key={i} sx={{ fontSize: 18, color: "#f59e0b" }} />
            ))}
          </span>
        );
      } else if (entryType(entry) === "percentage") {
        // Render number with % sign for percentage type
        numericDisplay = <span className="font-semibold">{numericValue}%</span>;
      } else if (entryType(entry) === "dollar") {
        // Render number with $ sign for dollar type
        numericDisplay = <span className="font-semibold">${numericValue}</span>;
      } else {
        // Render bold number for other types
        numericDisplay = <span className="font-semibold">{numericValue}</span>;
      }
    }

    // Render combined value
    if (numericDisplay && entry.value_text) {
      return (
        <div className="flex items-center gap-2">
          {numericDisplay}
          <span className="text-slate-600">-</span>
          <span className="whitespace-pre-line">{highlightSearchText(entry.value_text)}</span>
        </div>
      );
    } else if (numericDisplay) {
      return numericDisplay;
    } else if (entry.value_text) {
      return <span className="whitespace-pre-line">{highlightSearchText(entry.value_text)}</span>;
    } else {
      return "—";
    }
  };

  // Plain-text version of formatEntryValue for CSV export
  const formatEntryValueText = (entry) => {
    let numericValue = null;
    let numericDisplay = null;

    if (entry.value_integer !== null && entry.value_integer !== undefined && entry.value_integer !== "") {
      numericValue = parseInt(entry.value_integer);
    } else if (entry.value_float !== null && entry.value_float !== undefined && entry.value_float !== "") {
      numericValue = parseFloat(entry.value_float);
    }

    if (numericValue !== null) {
      if (entryType(entry) === "star") {
        numericDisplay = `${numericValue}/5`;
      } else if (entryType(entry) === "percentage") {
        numericDisplay = `${numericValue}%`;
      } else if (entryType(entry) === "dollar") {
        numericDisplay = `$${numericValue}`;
      } else {
        numericDisplay = String(numericValue);
      }
    }

    if (numericDisplay && entry.value_text) {
      return `${numericDisplay} - ${entry.value_text}`;
    } else if (numericDisplay) {
      return numericDisplay;
    } else if (entry.value_text) {
      return entry.value_text;
    }
    return "";
  };

  const exportCSV = () => {
    if (sortedData.length === 0) return;

    const rows = sortedData.map((entry) => ({
      "Created At": entry.created_at_formatted,
      "Date": entry.date,
      "Store": entry.store.name,
      "Category": entry.category.name,
      "Type": entryType(entry),
      "Value": formatEntryValueText(entry),
      "Author": entry.author ? entry.author.full_name : "N/A",
      "Files": entry.file_uploads.length,
    }));

    const csv = objectToCSV(rows);
    downloadCSV(
      csv,
      `logbook_report_${formatDate(selectedStartDate)}_to_${formatDate(selectedEndDate)}.csv`
    );
  };

  // Type badge colors
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "text":
        return "bg-blue-100 text-blue-700";
      case "star":
        return "bg-orange-100 text-orange-700";
      case "dollar":
        return "bg-green-100 text-green-700";
      case "percentage":
        return "bg-purple-100 text-purple-700";
      case "number":
        return "bg-rose-100 text-rose-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <>
      {showLightbox && (
        <div className="absolute w-screen h-screen z-40">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      {/* Rendered inside LogbookHubDisplay's Reports tab; the hub owns the
          page header, sub-tabs, and horizontal gutter. */}
      <div>
      {/* Filters Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Report Filters
          </h3>
          {/* Chat with logbook sits with the report, as in Analytics /
              Company Files ("Chat with reports" / "Chat with documents"). */}
          {props.onOpenAsk && (
            <button
              onClick={props.onOpenAsk}
              title="Chat with logbook"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white hover:border-violet-700 hover:text-violet-700 transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 16 }} />
              <span>Chat with logbook</span>
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          {/* Store Selection */}
          <div className="w-[235px]">
            <h4 className="text-slate-500 text-sm">Stores</h4>
            <div className="mt-1.5">
              <Select
                multiple
                value={selectedStores}
                onChange={handleStoreChange}
                renderValue={renderStoreValue}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      logbookEnabledStores.length > 0 &&
                      selectedStores.length === logbookEnabledStores.length
                    }
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <ListItemText
                    primary={
                      selectedStores.length === logbookEnabledStores.length
                        ? "Deselect All"
                        : "Select All"
                    }
                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  />
                </MenuItem>
                {logbookEnabledStores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    <Checkbox
                      checked={selectedStores.indexOf(store.id) > -1}
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": {
                          color: "#6d28d9",
                        },
                      }}
                    />
                    <ListItemText primary={store.store_name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Category Selection */}
          <div className="w-[235px]">
            <div className="flex items-center gap-2">
              <h4 className="text-slate-500 text-sm">Categories</h4>
              {loadingCategories && (
                <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
              )}
            </div>
            <div className="mt-1.5">
              <Select
                multiple
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={renderCategoryValue}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      availableCategories.length > 0 &&
                      selectedCategories.length === availableCategories.length
                    }
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <ListItemText
                    primary="Select All"
                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  />
                </MenuItem>
                {availableCategories.map((category) => (
                  <MenuItem key={category.uuid} value={category.uuid}>
                    <Checkbox
                      checked={selectedCategories.indexOf(category.uuid) > -1}
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": {
                          color: "#6d28d9",
                        },
                      }}
                    />
                    <ListItemText
                      primary={
                        <div className="flex items-center justify-between gap-2">
                          <span>{category.name}</span>
                          <span
                            className={`ml-4 text-xs px-1.5 py-0.5 rounded ${getTypeBadgeColor(
                              category.type
                            )}`}
                          >
                            {category.type}
                          </span>
                        </div>
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Author Selection */}
          <div className="w-[235px]">
            <div className="flex items-center gap-2">
              <h4 className="text-slate-500 text-sm">Authors</h4>
              {loadingAuthors && (
                <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
              )}
            </div>
            <div className="mt-1.5">
              <Select
                multiple
                value={selectedAuthors}
                onChange={handleAuthorChange}
                renderValue={renderAuthorValue}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      availableAuthors.length > 0 &&
                      selectedAuthors.length === availableAuthors.length
                    }
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <ListItemText
                    primary="Select All"
                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  />
                </MenuItem>
                {availableAuthors.map((author) => (
                  <MenuItem key={author.id} value={author.id}>
                    <Checkbox
                      checked={selectedAuthors.indexOf(author.id) > -1}
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": {
                          color: "#6d28d9",
                        },
                      }}
                    />
                    <ListItemText
                      primary={author.full_name}
                      secondary={`${author.entry_count} entries`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Date Range or Custom Dates */}
          {showCustomDateRange ? (
            <div className="flex gap-3">
              <div className="w-40">
                <h4 className="text-slate-500 text-sm">Start Date</h4>
                <div className="mt-1.5">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      value={selectedStartDate}
                      onChange={(newValue) => setSelectedStartDate(newValue)}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#6d28d9",
                            },
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          "& fieldset": {
                            borderRadius: "8px",
                            height: "45px",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      className="w-full"
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <div className="w-40">
                <h4 className="text-slate-500 text-sm">End Date</h4>
                <div className="mt-1.5">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      value={selectedEndDate}
                      onChange={(newValue) => setSelectedEndDate(newValue)}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#6d28d9",
                            },
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          "& fieldset": {
                            borderRadius: "8px",
                            height: "45px",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      className="w-full"
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <div className="h-full flex items-end justify-end">
                <Tooltip title="Back to preset date ranges">
                  <div
                    className="w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
                    onClick={() => toggleCustomDateRange(false)}
                  >
                    <UndoIcon />
                  </div>
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="w-[200px]">
              <h4 className="text-slate-500 text-sm">Date Range</h4>
              <div className="mt-1.5">
                <Select
                  fullWidth
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  input={<OutlinedInput />}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    paddingX: "15px",
                    height: "40px",
                    fontSize: "14px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                  className="w-full"
                >
                  {dateRangeMenuItems}
                </Select>
              </div>
            </div>
          )}

          {/* Search Query */}
          <div className="flex-1">
            <h4 className="text-slate-500 text-sm">Search Text</h4>
            <div className="mt-1.5">
              <TextField
                fullWidth
                placeholder="Search entry text..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputProps={{
                  autoComplete: "off",
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                    />
                  ),
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    fontSize: "14px",
                    "&.Mui-focused fieldset": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mt-4 space-y-2">
            {validationErrors.map((error, index) => (
              <div
                key={index}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
              >
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 flex gap-4">
          <LoadingButton
            variant="contained"
            loading={reportStatus === "loading"}
            loadingPosition="end"
            endIcon={
              reportStatus === "completed" ? <CheckIcon /> : <AutoGraphIcon />
            }
            onClick={generateReport}
            disabled={!isFormValid || reportStatus === "loading"}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "40px",
              backgroundColor:
                reportStatus === "completed" ? "green" : "#6d28d9",
              "&:hover": {
                backgroundColor:
                  reportStatus === "completed" ? "darkgreen" : "#5b21b6",
              },
            }}
          >
            {reportStatus === "loading"
              ? "Generating..."
              : reportStatus === "completed"
              ? "Report Generated"
              : "Generate Report"}
          </LoadingButton>

          {reportStatus === "completed" && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveReport}
              sx={{
                textTransform: "none",
                borderRadius: "50px",
                height: "40px",
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Save to Dashboard
            </Button>
          )}

          {reportStatus === "completed" && reportData.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportCSV}
              sx={{
                textTransform: "none",
                borderRadius: "50px",
                height: "40px",
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "rgba(109, 40, 217, 0.04)",
                },
              }}
            >
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      {reportStatus === "completed" && summaryData && (
        <div className="mt-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Report Summary
            </h3>
            <div className="flex gap-8 flex-wrap">
              <div>
                <p className="text-xs text-slate-500">Total Entries</p>
                <p className="text-2xl font-semibold text-slate-800">
                  {summaryData.total_entries}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Date Range</p>
                <p className="text-base text-slate-800">
                  {summaryData.date_range}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Stores</p>
                <p className="text-base text-slate-800">
                  {summaryData.stores_included}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Categories</p>
                <p className="text-base text-slate-800">
                  {summaryData.categories_included}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {reportStatus === "completed" && (
        <div className="mt-6">
          {reportData.length === 0 ? (
            <div className="bg-slate-50 rounded-lg p-8 text-center">
              <p className="text-lg text-slate-500">
                No entries found matching your criteria
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <TableSortLabel
                          active={orderBy === "created_at"}
                          direction={orderBy === "created_at" ? order : "asc"}
                          onClick={() => handleSort("created_at")}
                        >
                          Created At
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "store"}
                          direction={orderBy === "store" ? order : "asc"}
                          onClick={() => handleSort("store")}
                        >
                          Store
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "category"}
                          direction={orderBy === "category" ? order : "asc"}
                          onClick={() => handleSort("category")}
                        >
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "author"}
                          direction={orderBy === "author" ? order : "asc"}
                          onClick={() => handleSort("author")}
                        >
                          Author
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Files</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((entry) => (
                      <TableRow key={entry.uuid} hover>
                        <TableCell className="text-sm text-slate-600">
                          {entry.created_at_formatted}
                        </TableCell>
                        <TableCell>{entry.store.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span>{entry.category.name}</span>
                            {entry.category.description && (
                              <Tooltip
                                title={
                                  <div className="whitespace-pre-line">
                                    {entry.category.description}
                                  </div>
                                }
                                arrow
                              >
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 16, color: "#64748b", cursor: "help" }}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs px-2 py-1 rounded ${getTypeBadgeColor(
                              entryType(entry)
                            )}`}
                          >
                            {entryType(entry)}
                          </span>
                        </TableCell>
                        <TableCell>{formatEntryValue(entry)}</TableCell>
                        <TableCell>
                          {entry.author ? (
                            <Tooltip
                              title={`${entry.author.permission_type} • ${entry.author.initials}`}
                            >
                              <span>{entry.author.full_name}</span>
                            </Tooltip>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {entry.file_uploads.length > 0 ? (
                            loadingFileEntryId === entry.uuid ? (
                              <div className="flex items-center justify-center">
                                <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
                              </div>
                            ) : (
                              <div
                                className="flex items-center justify-center gap-1 cursor-pointer hover:opacity-70"
                                onClick={() => get_file_upload(entry.file_uploads[0], entry.uuid)}
                              >
                                <AttachFileIcon
                                  sx={{ fontSize: 16, color: "#6d28d9" }}
                                />
                                <span className="text-sm text-violet-700">
                                  {entry.file_uploads.length}
                                </span>
                              </div>
                            )
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {reportStatus === "error" && (
        <div className="mt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="text-lg font-semibold text-red-700">
              Failed to generate report
            </p>
            <p className="text-sm text-red-600 mt-2">
              Please try again or contact support if the problem persists.
            </p>
          </div>
        </div>
      )}

      {/* Save Report Modal */}
      <Modal
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        aria-labelledby="save-report-modal"
        aria-describedby="save-report-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            bgcolor: "background.paper",
            border: "2px solid #E2E8F0",
            borderRadius: "15px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setShowSaveDialog(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <SaveIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Save Logbook Report
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-4">
                Enter a name for this logbook report to save it for future use
                and add it to your dashboard.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Report Name
              </label>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Daily Team Wins Report"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />

              {reportTabs.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Organize in Tab (Optional)
                  </label>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedTabForReport || ""}
                    onChange={(e) =>
                      setSelectedTabForReport(e.target.value || null)
                    }
                    displayEmpty
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      padding: "5px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>No tab (All Reports)</em>
                    </MenuItem>
                    {reportTabs.map((tab) => (
                      <MenuItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReportConfirm}
                disabled={!reportName.trim() || saveStatus === "saving"}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                  ${
                    reportName.trim() && saveStatus !== "saving"
                      ? "bg-violet-700 text-white hover:bg-violet-800"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {saveStatus === "saving" ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
      </div>
    </>
  );
};

export default LogbookReportingDisplay;
